// T-spin puzzle generation, reusing the shared backtracking engine in
// core.ts. Two of the three target shapes are ported from the reference
// repo's dedicated T-spin pages:
//   - 'tsd'  — tspin-practice.html (script4.js): a standalone T-Spin Double,
//              structurally identical to downstack.ts's 'combotsd' overhang
//              but reserves no combo and additionally opens row 3 for entry.
//   - 'stsd' — advance-tspin-practice.html (script6.js), Config.mode 'stsd':
//              a "DT Cannon"-style hand-tuned double-notch wall producing two
//              T-Spin Doubles from one shared structure.
// 'tst' (T-Spin Triple) has no standalone mode in the reference repo (its
// T-spin pages only ever combine TSD with other techniques), so this is an
// original target shape built from the T-spin geometry directly: a 3-row-tall
// open shaft plus a one-row "ear" notch one column over, derived from the
// vertical-T orientation's cell offsets so the only way in is a real spin,
// not a straight drop. Validated empirically (see generator test notes) for
// a healthy generation success rate, not copied from any source file.

import { BOARD_HEIGHT, BOARD_WIDTH, type Cell, type PieceType } from '../engine/constants';
import { cloneBoard, createEmptyBoard, getMaxHeight, type Board, type TSpinResult } from '../engine/game';
import {
  DEFAULT_GEN_OPTIONS,
  addLine,
  allGrounded,
  generateADsMap,
  getShuffledHoldableQueue,
  splitHoldAndQueue,
  type GenOptions,
  type GenState,
  type TSpinMode,
} from './core';

export { DEFAULT_GEN_OPTIONS, splitHoldAndQueue, type TSpinMode };
export const TSPIN_MODES: TSpinMode[] = ['tsd', 'stsd', 'tst'];

export interface GeneratedTSpinPuzzle {
  board: Board;
  queue: PieceType[];
  mode: TSpinMode;
  pieceCount: number;
}

function generateFinalMap(gen: GenState, mode: TSpinMode): void {
  const board = gen.board;

  if (mode === 'tsd') {
    const height = Array.from({ length: BOARD_WIDTH }, () => Math.floor(Math.random() * 3) + 2);
    const tsdCol = Math.floor(Math.random() * 8) + 1;
    height[tsdCol] = 0;
    let isLeft: boolean;
    if (tsdCol === 1) isLeft = false;
    else if (tsdCol === 8) isLeft = true;
    else isLeft = Math.random() < 0.5;
    const sign = isLeft ? 1 : -1;
    height[tsdCol + sign] = 1;
    for (let row = 0; row < BOARD_HEIGHT; row++)
      for (let col = 0; col < BOARD_WIDTH; col++) if (row < height[col]) board[row][col] = 'G';
    board[1][tsdCol - sign] = 'N';
    if (isLeft) {
      board[2][tsdCol - 1] = 'G';
      board[0][tsdCol - 2] = 'G';
      board[1][tsdCol - 2] = 'G';
      board[2][tsdCol - 2] = 'G';
    } else {
      board[2][tsdCol + 1] = 'G';
      board[0][tsdCol + 2] = 'G';
      board[1][tsdCol + 2] = 'G';
      board[2][tsdCol + 2] = 'G';
    }
    const linesAdd = Math.floor(Math.random() * 3);
    const isTsdCol = Math.random() < 0.5;
    let colAdd = isTsdCol ? tsdCol : Math.floor(Math.random() * BOARD_WIDTH);
    if (colAdd === tsdCol - sign || colAdd === tsdCol - 2 * sign) colAdd = tsdCol;
    board[2][colAdd] = 'N';
    board[3][colAdd] = 'N'; // tspin-practice opens one more row than combo-into-tsd, for entry room with no combo lead-in
    for (let rowIdx = 0; rowIdx < linesAdd; rowIdx++) {
      addLine(gen, rowIdx);
      board[rowIdx][colAdd] = 'N';
    }
    gen.addedLine = [];
    return;
  }

  if (mode === 'stsd') {
    const height = Array.from({ length: BOARD_WIDTH }, () => Math.floor(Math.random() * 3) + 5);
    const isRight = Math.random() < 0.5; // "is_right" in the source: overhang opens toward lower columns
    const tsdCol = Math.floor(Math.random() * 6) + 2; // 2..7
    if (Math.random() < 0.5) {
      for (let i = 0; i < BOARD_WIDTH; i++) height[i] += 1;
      if (isRight) {
        height[tsdCol - 1] -= 1;
        height[tsdCol - 2] -= 1;
      } else {
        height[tsdCol + 1] -= 1;
        height[tsdCol + 2] -= 1;
      }
    }
    for (let row = 0; row < BOARD_HEIGHT; row++)
      for (let col = 0; col < BOARD_WIDTH; col++) if (row < height[col]) board[row][col] = 'G';

    let kickCol: number;
    if (isRight) {
      const garbagePos = ['GNNG', 'GNNG', 'GGNG', 'NNNG', 'NNGG'];
      for (let row = 0; row < 5; row++) for (let col = 0; col < 4; col++) board[row][tsdCol - 2 + col] = garbagePos[row][col] as Cell;
      kickCol = tsdCol - 3;
      board[5][tsdCol - 2] = 'N';
      board[5][tsdCol - 1] = 'N';
      board[6][tsdCol - 2] = 'N';
      board[6][tsdCol - 1] = 'N';
    } else {
      const garbagePos = ['GNNG', 'GNNG', 'GNGG', 'GNNN', 'GGNN'];
      for (let row = 0; row < 5; row++) for (let col = 0; col < 4; col++) board[row][tsdCol - 1 + col] = garbagePos[row][col] as Cell;
      kickCol = tsdCol + 3;
      board[5][tsdCol + 1] = 'N';
      board[5][tsdCol + 2] = 'N';
      board[6][tsdCol + 1] = 'N';
      board[6][tsdCol + 2] = 'N';
    }
    if (kickCol >= 0 && kickCol < BOARD_WIDTH) {
      board[3][kickCol] = 'G';
      board[4][kickCol] = 'G';
    }

    const linesAdd = Math.floor(Math.random() * 3) + 2;
    const colOffset = Math.floor(Math.random() * 2) + 1;
    const colAdd = isRight ? tsdCol - colOffset : tsdCol + colOffset;
    for (let rowIdx = 0; rowIdx < linesAdd; rowIdx++) {
      addLine(gen, rowIdx);
      board[rowIdx][colAdd] = 'N';
    }
    gen.addedLine = [];
    return;
  }

  // 'tst': a 3-row-tall open shaft (gapCol) plus a one-row "ear" notch in the
  // adjacent column — derived from the vertical-T cell offsets (top/mid/bottom
  // in gapCol, one more mid-row cell in earCol) so only a real spin reaches it.
  const height = Array.from({ length: BOARD_WIDTH }, () => Math.floor(Math.random() * 3) + 3); // 3-5: guarantees rows 0-2 filled by default
  const gapCol = Math.floor(Math.random() * 8) + 1;
  height[gapCol] = 0;
  let isLeft: boolean;
  if (gapCol === 1) isLeft = false;
  else if (gapCol === 8) isLeft = true;
  else isLeft = Math.random() < 0.5;
  const sign = isLeft ? 1 : -1;
  const earCol = gapCol + sign;
  for (let row = 0; row < BOARD_HEIGHT; row++)
    for (let col = 0; col < BOARD_WIDTH; col++) if (row < height[col]) board[row][col] = 'G';
  board[1][earCol] = 'N'; // the ear: filled at row0 and row2 (from height>=3), open only at row1

  const linesAdd = Math.floor(Math.random() * 3);
  const useGapCol = Math.random() < 0.5;
  let colAdd = useGapCol ? gapCol : Math.floor(Math.random() * BOARD_WIDTH);
  if (colAdd === earCol) colAdd = gapCol;
  board[2][colAdd] = 'N';
  for (let rowIdx = 0; rowIdx < linesAdd; rowIdx++) {
    addLine(gen, rowIdx);
    board[rowIdx][colAdd] = 'N';
  }
  gen.addedLine = [];
}

// stsd's per-filler-piece success rate is far lower than tsd/tst's (it's
// chaining placements into a hand-tuned DT-Cannon template, not an open
// notch), so the odds of completing a full chain collapse as the chain gets
// longer — empirically ~0% at unreserved=5 (pieceCount 7, the slider's
// default) vs. a reliable ~25-35% at unreserved<=3. Cap it there regardless
// of the piece-count slider rather than let it silently stop generating.
const STSD_MAX_UNRESERVED = 3;

function unreservedCountFor(mode: TSpinMode, pieceCount: number): number {
  if (mode === 'stsd') return Math.min(pieceCount - 2, STSD_MAX_UNRESERVED);
  return pieceCount - 1;
}

function appendReservedPieces(mode: TSpinMode, queue: PieceType[]): PieceType[] {
  if (mode === 'stsd') return [...queue, 'T', 'T'];
  return [...queue, 'T'];
}

export function generateTSpinPuzzle(mode: TSpinMode, opts: GenOptions): GeneratedTSpinPuzzle {
  // 'stsd' reserves 2 T's — guard against a degenerate (<=0) filler count if
  // the UI's piece-count minimum (2) is used with it.
  const unreservedCount = Math.max(1, unreservedCountFor(mode, opts.pieceCount));

  const gen: GenState = {
    board: createEmptyBoard(),
    piece: { piece: 'T', x: 4, y: 18, orientation: 0 },
    addedLine: [],
    boardHistory: [],
    pieceAdded: [],
    finishedMap: createEmptyBoard(),
    // 'moreSkims' (false by default) picks addRandomLineLessSkim, which finds
    // its insertion row by scanning for a "cheese" (single-gap) prefix. The
    // tall, multi-notch stsd/tst target shapes never look like cheese even
    // before any lines are added, so that scan degenerates to always
    // inserting at row 0 and the generator never progresses. Force the
    // (max-height-based) skim inserter for these two regardless of the
    // option — empirically verified necessary, see generator test notes.
    moreSkims: mode === 'stsd' || mode === 'tst' ? true : opts.moreSkims,
    moreHoles: opts.moreHoles,
    lastInfo: 'null',
  };

  generateFinalMap(gen, mode);
  gen.finishedMap = cloneBoard(gen.board);

  for (let i = 0; i < 999; i++) {
    if (i === 50) {
      if (mode !== 'stsd' && mode !== 'tst') gen.moreSkims = false;
      // Matches script6.js's advance-tspin-practice fallback: the taller,
      // multi-notch shapes (DT-Cannon-style 'stsd', and 'tst' for the same
      // structural reason) are close to unsatisfiable at a strict 0-hole
      // tolerance, so relax it once the easy attempts are exhausted.
      if (mode === 'stsd' || mode === 'tst') gen.moreHoles = true;
    }
    const solved = generateADsMap(gen, unreservedCount, mode, opts, unreservedCount) && getMaxHeight(gen.board) < 17;
    if (solved) {
      const queue = appendReservedPieces(mode, [...gen.pieceAdded].reverse());
      const shuffledQueue = getShuffledHoldableQueue(queue);
      if (shuffledQueue.length > 0) {
        const startBoard = cloneBoard(gen.boardHistory[gen.boardHistory.length - 1]);
        for (let row = 0; row < BOARD_HEIGHT; row++)
          for (let col = 0; col < BOARD_WIDTH; col++) if (startBoard[row][col] !== 'G') startBoard[row][col] = 'N' as Cell;
        return { board: startBoard, queue: shuffledQueue, mode, pieceCount: opts.pieceCount };
      }
    } else if (i % 2 === 1) {
      generateFinalMap(gen, mode);
      gen.finishedMap = cloneBoard(gen.board);
      gen.pieceAdded = [];
      gen.boardHistory = [];
    }
  }
  throw new Error('T-spin puzzle generation failed after 999 attempts — try different options.');
}

/** Tracks T-spin clears across a puzzle attempt (win conditions below check
 * the whole attempt, not just the last piece — matching the reference
 * engine's Record.done_tsd/done_tst, which are likewise cumulative). */
export interface TSpinProgress {
  tss: number;
  tsd: number;
  tst: number;
}

export function createTSpinProgress(): TSpinProgress {
  return { tss: 0, tsd: 0, tst: 0 };
}

export function recordTSpinClear(progress: TSpinProgress, linesCleared: number, tspin: TSpinResult): void {
  if (!tspin) return;
  if (linesCleared === 1) progress.tss += 1;
  else if (linesCleared === 2) progress.tsd += 1;
  else if (linesCleared === 3) progress.tst += 1;
}

/**
 * 'tsd' needs one T-Spin Double and a fully cleared board (matches
 * tspin-practice.html). 'tst' needs one T-Spin Triple. 'stsd' is the
 * reference repo's "two T-Spin Doubles" DT-Cannon win condition, relaxed per
 * request: the first clear must be a double, the second may be either a
 * double or a single.
 */
export function isTSpinWinningState(mode: TSpinMode, board: Board, progress: TSpinProgress): boolean {
  switch (mode) {
    case 'tsd':
      return progress.tsd >= 1 && allGrounded(board);
    case 'tst':
      return progress.tst >= 1;
    case 'stsd':
      return progress.tsd >= 1 && progress.tsd + progress.tss >= 2;
    default:
      return false;
  }
}
